import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import useContacts from "../../components/ContactsList/use-contacts";
import { useToast } from "../../components";
import OmnichannelService, {
  IUserInfo
} from "../../services/omnichannelService";
import { blobToBase64 } from "../../util/blobToBase64";
import { iDialogMessagesFile } from "../use-omnichannel";
import TopBarChatInfos from "./top-bar-chat-infos";
import ChatInfosBody from "./chat-infos-body";

export interface ChatContactInfosProps {
  dialog: iDialogMessagesFile;
  onContactUpdated?: () => Promise<void>;
  refreshKey?: number;
}

const ChatContactInfos = ({
  dialog,
  onContactUpdated,
  refreshKey = 0
}: ChatContactInfosProps) => {
  const toast = useToast();
  const { getContact } = OmnichannelService();
  const { LoadWhatsappAvatar, UpdateContacts } = useContacts();
  const [contactInfo, setContactInfo] = useState<IUserInfo | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFileId, setAvatarFileId] = useState<number | null>(null);
  const [avatarBase64, setAvatarBase64] = useState("");
  const [avatarFileName, setAvatarFileName] = useState("");
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarRemoved, setAvatarRemoved] = useState(false);

  useEffect(() => {
    const getData = async () => {
      try {
        const data = await getContact(dialog.id);
        setContactInfo(data);
        setAvatarPreview(data.avatarUrl);
        setAvatarFileId(null);
        setAvatarBase64("");
        setAvatarFileName("");
        setAvatarRemoved(false);
      } catch (error) {
        toast?.current?.show({
          life: 3000,
          severity: "error",
          summary: "Erro.",
          detail:
            "Erro ao buscar dados do usuário. Por favor tente mais tarde.",
          sticky: false
        });
      }
    };

    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog.id, refreshKey]);

  const handleLoadWhatsappAvatar = async () => {
    if (!contactInfo?.phone) {
      toast?.current?.show({
        life: 5000,
        severity: "warn",
        summary: "Atenção",
        detail: "Contato sem telefone."
      });
      return;
    }

    if (!dialog.id) {
      toast?.current?.show({
        life: 5000,
        severity: "warn",
        summary: "Atenção",
        detail: "Conversa não encontrada para buscar a foto do WhatsApp."
      });
      return;
    }

    try {
      setAvatarLoading(true);
      const avatar = await LoadWhatsappAvatar({
        phone: contactInfo.phone,
        dialogId: dialog.id
      });

      setAvatarFileId(null);
      setAvatarPreview(avatar.avatarBase64);
      setAvatarBase64(avatar.avatarBase64);
      setAvatarFileName(avatar.avatarFileName);
      setAvatarRemoved(false);
    } catch (error: any) {
      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail:
          error?.message ||
          error?.response?.data?.message ||
          "Não foi possível carregar a foto do WhatsApp."
      });
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleAvatarUpload = async (file?: File) => {
    if (!file) {
      return;
    }

    const base64 = await blobToBase64(file);
    setAvatarFileId(null);
    setAvatarPreview(String(base64));
    setAvatarBase64(String(base64));
    setAvatarFileName(file.name);
    setAvatarRemoved(false);
  };

  const handleRemoveAvatar = () => {
    setAvatarPreview(null);
    setAvatarFileId(null);
    setAvatarBase64("");
    setAvatarFileName("");
    setAvatarRemoved(true);
  };

  const handleSaveContact = async (data: {
    name: string;
    phone: string;
    email: string | null;
  }) => {
    if (!contactInfo?.workspaceId) {
      toast?.current?.show({
        life: 5000,
        severity: "warn",
        summary: "Atenção",
        detail:
          "É preciso atribuir a conversa a alguma workspace para poder editar o contato."
      });
      return;
    }

    if (!contactInfo?.id) {
      toast?.current?.show({
        life: 5000,
        severity: "warn",
        summary: "Atenção",
        detail: "Contato não encontrado para edição."
      });
      return;
    }

    try {
      const avatarChanged =
        avatarRemoved || Boolean(avatarBase64) || avatarFileId !== null;

      await UpdateContacts(contactInfo.id, {
        ...data,
        ...(avatarChanged
          ? { avatarFileId: avatarRemoved ? null : avatarFileId }
          : {}),
        avatarBase64: avatarBase64 || undefined,
        avatarFileName: avatarFileName || undefined
      });

      setContactInfo({
        ...contactInfo,
        name: data.name,
        phone: data.phone,
        email: data.email,
        avatarUrl: avatarPreview
      });
      setAvatarFileId(null);
      setAvatarBase64("");
      setAvatarFileName("");
      setAvatarRemoved(false);
      await onContactUpdated?.();

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso",
        detail: "Contato atualizado com sucesso."
      });
    } catch (error: any) {
      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail:
          error?.message ||
          error?.response?.data?.message ||
          "Não foi possível atualizar o contato."
      });
    }
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        height: 100vh;
        width: 20rem;
        background: #ffffff;
        border-left: 1px solid #e5e5e5;

        @media screen and (max-width: 599px) {
          width: 100%;
        }
        @media screen and (max-width: 400px) {
          width: 15rem;
        }
      `}
    >
      <TopBarChatInfos
        dialog={dialog}
        contactName={contactInfo?.name}
        avatarUrl={avatarPreview}
        avatarLoading={avatarLoading}
        onLoadWhatsappAvatar={handleLoadWhatsappAvatar}
        onAvatarUpload={handleAvatarUpload}
        onRemoveAvatar={handleRemoveAvatar}
      />

      {/* <ChatDates createdAt="" updatedAt="" /> */}

      <ChatInfosBody
        dialog={dialog}
        contactInfo={contactInfo}
        onSaveContact={handleSaveContact}
      />
    </div>
  );
};

export default ChatContactInfos;
